import { Button, DropDownButton, DropDownButtonItem, DropDownButtonItemClickEvent } from '@progress/kendo-react-buttons';
import { TabStrip, TabStripTab } from '@progress/kendo-react-layout';
import React, { useMemo } from 'react';
import {
    ChartsLinePointsOutline,
    DeltaOutline,
    ExpandOutline,
    MenuOverflowVerticalSolid,
    MinimizeOutline,
    PinOutline,
    PinSolid
} from '../../../../icons/icons';
import { NoteCategory } from '../../../../types/notes';
import { EventBus } from '../../../contexts/event-bus';
import WorkspaceItemContext from '../../../contexts/workspace/workspace-item-context';
import { useAppDispatch, useAppSelector } from '../../../hooks/store-hooks';
import { notesSelector } from '../../../state/selectors';
import { addPinnedFlight, changeFlightDetailsTabIndex, removePinnedFlight, toggleLeftFlyout } from '../../../state/slices/workspace-slice';
import { FlyoutActions, FlyoutTitle } from '../flyouts/Flyout';
import Notes from '../flyouts/Notes';
import FlightHeader from './FlightHeader';
import FlightImpacts from './FlightImpacts';
import FlightSummary from './FlightSummary';
import { FlightHistoryTelerikWorks } from './history/FlightHistoryTelerikWorks';

export default function FlightDetails() {
    const dispatch = useAppDispatch();
    const ctx = React.useContext(WorkspaceItemContext);
    const selectedFlight = useAppSelector((x) => x.workspace.items[ctx.id].flights.selected[0]);
    const flight = selectedFlight?.details;
    const ids = useMemo(() => {
        return [flight?.acid, flight?.regId].filter((id): id is string => id !== undefined && id !== null);
    }, [flight?.acid, flight?.regId]);
    const notes = useAppSelector((state) => {
        return notesSelector(state, ids);
    });
    const selectedTab = useAppSelector((x) => x.workspace.items[ctx.id].flights.settings.detailsTabIndex) ?? 0;
    const impacts = selectedFlight?.impacts;
    const pinnedFlights = useAppSelector((x) => x.workspace.flights.pinned);
    const [expanded, setExpanded] = React.useState(true);
    const isPinned = pinnedFlights.some((x) => x.id === flight?.id);

    const togglePinned = React.useCallback(() => {
        if (isPinned) {
            dispatch(removePinnedFlight({ workspaceId: ctx.id, flightId: flight.id }));
        } else {
            dispatch(addPinnedFlight({ workspaceId: ctx.id, flightId: flight.id }));
        }
    }, [isPinned, flight?.id]);

    const handleDropdownButtonClicked = React.useCallback(
        (e: DropDownButtonItemClickEvent) => {
            if (e.itemIndex === 0) {
                // ED
                dispatch(toggleLeftFlyout({ type: 'diversion', flightId: flight.id }));
            } else if (e.itemIndex === 1) {
                // RA
                if (flight) {
                    EventBus.fire('route-analyzer-load-flight', flight.id);
                }
            }
        },
        [flight]
    );

    const onAirportClicked = React.useCallback(
        (id: string) => {
            dispatch(toggleLeftFlyout({ type: 'airport', airportId: id }));
        },
        [dispatch]
    );

    if (!flight) {
        return <></>;
    }

    return (
        <>
            <FlyoutActions>
                <DropDownButton
                    title="More tools"
                    className="button-small"
                    fillMode={'flat'}
                    svgIcon={MenuOverflowVerticalSolid}
                    onItemClick={handleDropdownButtonClicked}>
                    <DropDownButtonItem svgIcon={DeltaOutline} text="Emergency diversion"></DropDownButtonItem>
                    <DropDownButtonItem svgIcon={ChartsLinePointsOutline} text="Analyze routes"></DropDownButtonItem>
                </DropDownButton>

                <Button
                    className="button-small"
                    fillMode={'flat'}
                    title="Pin"
                    id="flight-summary-pin-btn"
                    svgIcon={isPinned ? PinSolid : PinOutline}
                    onClick={togglePinned}></Button>
                <Button
                    className="button-small"
                    fillMode={'flat'}
                    title={expanded ? 'Collapse' : 'Expand'}
                    svgIcon={expanded ? MinimizeOutline : ExpandOutline}
                    onClick={() => setExpanded(!expanded)}></Button>
            </FlyoutActions>

            <FlyoutTitle>
                {flight?.acid} - {flight?.acType}
                {import.meta.env.DEV && <span className="ms-2 secondary-text font-mono">({flight.id})</span>}
            </FlyoutTitle>

            {!expanded && <FlightHeader flight={flight} airportSummaryLinks={true} onAirportClicked={onAirportClicked} />}
            {expanded && (
                <TabStrip
                    tabAlignment={'stretched'}
                    className="d-flex flex-column h-100 min-h-0"
                    selected={selectedTab}
                    onSelect={(e) => dispatch(changeFlightDetailsTabIndex(e.selected))}
                    tabContentStyle={{ padding: 0 }}
                    size={'medium'}
                    animation={false}>
                    <TabStripTab title="Summary">
                        <>
                            <FlightHeader
                                id="flight-summary-header"
                                flight={flight}
                                airportSummaryLinks={true}
                                onAirportClicked={onAirportClicked}
                            />
                            <FlightSummary flight={selectedFlight} />
                        </>
                    </TabStripTab>

                    <TabStripTab title="History" contentClassName="flight-history-tab">
                        <FlightHistoryTelerikWorks id={flight.id} />
                    </TabStripTab>

                    <TabStripTab
                        title={
                            <div className="d-flex gap-1 align-items-center pe-0">
                                Impacts
                                {impacts.length > 0 && (
                                    <span className="fe-badge icon-b-1 p-1 py-0 align-self-center">{impacts.length}</span>
                                )}
                            </div>
                        }>
                        <FlightImpacts impacts={impacts} />
                    </TabStripTab>

                    <TabStripTab
                        contentClassName={'notes-details-tab'}
                        title={
                            <div id="flight-details-notes-tab" className="d-flex gap-1 align-items-center pe-0">
                                Notes
                                {notes && notes.length > 0 && <span className="fe-badge p-1 py-0 align-self-center">{notes.length}</span>}
                            </div>
                        }>
                        <Notes
                            itemId={selectedFlight.details.acid}
                            additionalItemId={selectedFlight.details.regId ?? undefined}
                            category={NoteCategory.aircraft}
                            flight={selectedFlight.details}
                        />
                    </TabStripTab>
                </TabStrip>
            )}
        </>
    );
}
